/**
 * src/lib/data/users.ts — User CRUD HTTP wrappers (Phase 5.2 wired).
 *
 * Hits the worker /api/users routes added in sub-step 5.2.
 *
 * Contract:
 *   - listUsers       — open read per §1.4; no rbac check
 *   - getUser         — open read
 *   - createUser      — ADMIN only (asserted client-side; worker re-checks)
 *   - updateUser      — ADMIN for metadata fields, self+ADMIN for display
 *   - deactivateUser  — ADMIN only
 *
 * Every write threads the viewer through for the future X-User-Id header
 * (Phase 5.6) + future audit log (Phase 5.7).
 */
import type { User, UserCreate, UserUpdate } from "./types";
import {
  assertCanWriteUserDisplayFields,
  assertCanWriteUserMetadata,
} from "./rbac";

/**
 * Preferred URL slug for a user (Phase 6.3).
 *
 * Mirrors the worker's resolveUserByIdentifier resolution order so a slug
 * we generate here is guaranteed to round-trip. Preference:
 *   1. HubSpot owner ID — non-sequential, stable, present on most users.
 *   2. Email local-part — readable fallback for users without HubSpot.
 *   3. Canonical User.id — opaque last resort.
 *
 * Used by ViewerChip / MeRedirect / AdminUsers row links to build /u/...
 * URLs that aren't trivially iterable but still legible internally.
 */
export function canonicalUserSlug(user: Pick<User, "id" | "email" | "hubspotOwnerId">): string {
  if (user.hubspotOwnerId && /^[0-9]+$/.test(String(user.hubspotOwnerId))) {
    return String(user.hubspotOwnerId);
  }
  const local = (user.email || "").split("@")[0].trim().toLowerCase();
  if (local && /^[a-z0-9._-]+$/.test(local)) return local;
  return user.id;
}

export interface UserFetchOptions {
  workerUrl: string;
  viewPassword: string;
  /** Required for admin operations (the worker enforces). */
  editPassword?: string;
  /** Viewer User.id, sent as X-User-Id (Phase 5.6 will use this). */
  viewerUserId?: string;
  signal?: AbortSignal;
}

export class UserApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "UserApiError";
    this.status = status;
    this.body = body;
  }
}

function authHeaders(opts: UserFetchOptions, withEdit = false): Record<string, string> {
  const h: Record<string, string> = {
    "X-View-Password": opts.viewPassword,
  };
  if (withEdit && opts.editPassword) h["X-Edit-Password"] = opts.editPassword;
  if (opts.viewerUserId) h["X-User-Id"] = opts.viewerUserId;
  return h;
}

async function parseOrThrow(resp: Response, op: string): Promise<any> {
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new UserApiError(
      `${op} failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return body;
}

export async function listUsers(opts: UserFetchOptions): Promise<User[]> {
  const resp = await fetch(`${opts.workerUrl}/api/users`, {
    headers: authHeaders(opts),
    signal: opts.signal,
  });
  const data = await parseOrThrow(resp, "listUsers");
  return Array.isArray(data) ? data : [];
}

export async function getUser(id: string, opts: UserFetchOptions): Promise<User | null> {
  const resp = await fetch(`${opts.workerUrl}/api/users/${encodeURIComponent(id)}`, {
    headers: authHeaders(opts),
    signal: opts.signal,
  });
  if (resp.status === 404) return null;
  return await parseOrThrow(resp, "getUser");
}

export async function createUser(viewer: User, input: UserCreate, opts: UserFetchOptions): Promise<User> {
  assertCanWriteUserMetadata(viewer);
  const resp = await fetch(`${opts.workerUrl}/api/users`, {
    method: "POST",
    headers: { ...authHeaders(opts, /*withEdit*/ true), "Content-Type": "application/json" },
    body: JSON.stringify(input),
    signal: opts.signal,
  });
  return await parseOrThrow(resp, "createUser");
}

export async function updateUser(
  viewer: User,
  userId: string,
  patch: UserUpdate,
  opts: UserFetchOptions,
): Promise<User> {
  // §1.5 matrix, Phase 5.5b shape: display fields (name/email/label) are
  // self+ADMIN editable; everything else is ADMIN-only metadata. Predates
  // the level/label/isAdmin/marketIds rename — the old field names here
  // (role, marketId) silently bypassed the gate. Server enforces edit
  // password regardless; this is the client-side intent assertion.
  const editsDisplay  = "name" in patch || "email" in patch || "label" in patch;
  const editsMetadata =
    "level" in patch || "marketIds" in patch || "hubspotOwnerId" in patch ||
    "commissionPlanRef" in patch || "isAdmin" in patch || "active" in patch;
  if (editsMetadata) assertCanWriteUserMetadata(viewer);
  if (editsDisplay) assertCanWriteUserDisplayFields(viewer, userId);
  const resp = await fetch(`${opts.workerUrl}/api/users/${encodeURIComponent(userId)}`, {
    method: "PUT",
    headers: { ...authHeaders(opts, true), "Content-Type": "application/json" },
    body: JSON.stringify(patch),
    signal: opts.signal,
  });
  return await parseOrThrow(resp, "updateUser");
}

export async function deactivateUser(viewer: User, userId: string, opts: UserFetchOptions): Promise<User> {
  assertCanWriteUserMetadata(viewer);
  const resp = await fetch(`${opts.workerUrl}/api/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    headers: authHeaders(opts, true),
    signal: opts.signal,
  });
  return await parseOrThrow(resp, "deactivateUser");
}
